import { useEffect, useMemo, useState } from 'react'
import { tenantBillingPlanGetSpec, type BillingFeatureRow, type BillingPlanRow } from 'aevocam-rest-api-spec'
import { useApi } from '@/hooks/useApi'
import { DataGrid } from '@/components/dataGrid/DataGrid'
import type { DataGridState } from '@/components/dataGrid/createDataGridState'
import type { DataGridColumn } from '@/components/dataGrid/DataGrid'
import { Spinner } from '@/components/graphics/Spinner'

type FeatureGridRow = BillingFeatureRow & { _key: string }

type Props = {
  tenantId: string
}

export function TenantBillingPlan({ tenantId }: Props) {
  const api = useApi({ requireLogin: true })
  const sessionInitialized = api.status.initialized
  const isLoggedIn = !!api.status.session?.loggedIn

  const [plan, setPlan] = useState<BillingPlanRow | null>(null)
  const [features, setFeatures] = useState<BillingFeatureRow[]>([])
  const [loading, setLoading] = useState(true)

  const [gridState, setGridState] = useState<DataGridState<FeatureGridRow>>({
    rows: [],
    totalRows: 0,
    skip: 0,
    take: 10,
  })

  //--------------------------------------------------------------------------------
  const gatedTenantId = sessionInitialized && isLoggedIn
    ? tenantId
    : null

  useEffect(() => {
    if (!tenantId) return
    if (!sessionInitialized) {
      setLoading(true)
      return
    }
    if (!isLoggedIn) {
      setLoading(true)
      return
    }

    let cancelled = false
    setLoading(true)
    api
      .request(tenantBillingPlanGetSpec, { path: { tenant: tenantId } })
      .then(res => {
        if (cancelled) return
        const plan = res.data?.plan ?? null
        const features = res.data?.features ?? []
        setPlan(plan)
        setFeatures(features)
      })
      .catch(() => {
        if (cancelled) return
        setPlan(null)
        setFeatures([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [gatedTenantId, api])

  //--------------------------------------------------------------------------------
  useEffect(() => {
    const rows: FeatureGridRow[] = features.map((f, i) => ({
      ...f,
      _key: String(f.id ?? `f-${i}`),
    }))
    setGridState({
      rows,
      totalRows: rows.length,
      skip: 0,
      take: Math.max(rows.length, 1),
    })
  }, [features])

  //--------------------------------------------------------------------------------
  const columns = useMemo<DataGridColumn<FeatureGridRow>[]>(() => [
    { id: 'section', label: 'Section' },
    { id: 'name', label: 'Feature' },
    { id: 'units', label: 'Units', render: (v: string | undefined) => v ?? '-' },
    {
      id: 'rate',
      label: 'Rate',
      align: 'right',
      render: (v: number | undefined, { row }) => {
        const rate = v ?? row.rate
        const perUnit = row.perUnit
        if (rate == null) return '—'
        const suffix = perUnit ? ` / ${perUnit}` : ''
        return `$${rate}${suffix}`
      },
    },
  ], [])

  //--------------------------------------------------------------------------------
  if (loading) {
    return (
      <div className='Cutout Wrapped'>
        <h2>Billing plan</h2>
        <Spinner /> Loading…
      </div>
    )
  }

  //--------------------------------------------------------------------------------
  if (!plan && features.length === 0) {
    return (
      <div className='Cutout Wrapped'>
        <h2>Billing plan</h2>
        <p>No billing plan data.</p>
      </div>
    )
  }

  //--------------------------------------------------------------------------------
  if (!plan || !features.length) return null

  //--------------------------------------------------------------------------------
  return (
    <div className='Cutout Wrapped' style={{ maxWidth: '31rem' }}>
      <h2>Billing plan rates</h2>
      <div style={{ width: 'fit-content' }}>
        <DataGrid<FeatureGridRow>
          idColumn='id'
          columns={columns}
          state={gridState}
          setState={setGridState}
        />
      </div>
    </div>
  )
}
